<template>
    <v-flex>
        <card-collection
            class="px-0-nested"
            :meta="meta"
            :busy="overlay"
            @updated="updated"
        >
            <template #data>
                <thead>
                <tr>
                    <th>{{ t('main_goal_label_id') }}</th>
                    <th>{{ t('main_goal_label_title') }}</th>
                    <th>{{ t('main_goal_label_type') }}</th>
                    <th>{{ t('main_goal_label_plan_type') }}</th>
                    <th>{{ t('main_goal_label_status') }}</th>
                    <th>{{ t('main_goal_label_start_at') }}</th>
                    <th>{{ t('main_goal_label_end_at') }}</th>
                    <th>{{ t('main_goal_label_duration') }}</th>
                    <th>{{ t('main_goal_label_program') }}</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="collection.data.length === 0">
                    <td class="text-center" colspan="10">{{ t('general_text_no_data') }}</td>
                </tr>
                <template v-else v-for="goal in collection.data">
                    <tr>
                        <td>
                            <a href="javascript:" @click.prevent="touch(goal)">{{ goal.id }}</a>
                        </td>
                        <td>
                            <a href="javascript:" @click.prevent="touch(goal)">{{ goal.title }}</a>
                        </td>
                        <td>{{ mainGoalTypeTitle(goal.type) }}</td>
                        <td>{{ goal.planType ? planTypeTitle(goal.planType) : '-' }}</td>
                        <td>
                            <v-chip
                                small
                                :color="statusColor(goal.status)"
                                text-color="white"
                            >{{ mainGoalStatusTitle(goal.status) }}</v-chip>
                        </td>
                        <td>{{ goal.startAt ? goal.startAt.format('DD/MM/YYYY') : '-' }}</td>
                        <td>{{ goal.endAt ? goal.endAt.format('DD/MM/YYYY') : '-' }}</td>
                        <td>{{ goal.durationFormatted || '-' }}</td>
                        <td>{{ goal.program ? goal.program.name : '-' }}</td>
                        <td class="text-right">
                            <confirm-modal
                                :title="t('general_title_confirm_action')"
                                @agree="del(goal)"
                            >
                                <template #default="{ on, attrs }">
                                    <v-btn
                                        icon
                                        small
                                        color="orange"
                                        v-on="on"
                                        v-bind="attrs"
                                    ><v-icon small>mdi-delete-outline</v-icon></v-btn>
                                </template>
                            </confirm-modal>
                        </td>
                    </tr>
                </template>
                </tbody>
            </template>
        </card-collection>
    </v-flex>
</template>

<script lang="ts">
    import { defineComponent, reactive, computed, onMounted, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useEnums } from '@/core/defaults'
    import { UserMainGoalCollection } from '@/store/modules/user-main-goal-collection'
    import { UserMainGoal } from '@/core/models/user-main-goal'
    import { User } from '@/core/models/user'
    import CardCollection from '@/components/CardCollection.vue'
    import ConfirmModal from '@/components/ConfirmModal.vue'

    export default defineComponent({
        name: 'UserMainGoalCollection',

        components: {
            CardCollection,
            ConfirmModal,
        },

        props: {
            user: {
                type: User,
                required: true,
            },
        },

        setup(props, context) {
            const store = useStore()

            const state = reactive({
                overlay: true,
                meta: undefined,
            })

            const queries = reactive({
                page: 1,
                per_page: 20,
                sort: '-id',
            })

            const collection = computed(() => <UserMainGoalCollection>store.getters['userMainGoalCollection/get'])

            const capture = () => {
                state.overlay = true
                store.dispatch('userMainGoalCollection/get', {
                    userId: props.user.id,
                    params: queries,
                }).then((data: any) => {
                    state.meta = data.meta
                    state.overlay = false
                })
            }

            const updated = (updatedQueries: any) => {
                queries.page = updatedQueries.page
                capture()
            }

            const touch = (goal: UserMainGoal) => {
                context.emit('touched', goal)
            }

            const del = (goal: UserMainGoal) => {
                store.dispatch('userMainGoalCollection/delete', {
                    userId: props.user.id,
                    id: goal.id,
                }).then(() => {
                    store.dispatch('notify', { color: 'success', message: 'Deleted' })
                })
            }

            const statusColor = (status: string): string => {
                const map: Record<string, string> = {
                    draft: 'grey',
                    in_progress: 'primary',
                    finished: 'success',
                    abandoned: 'orange darken-2',
                }
                return map[status] || 'grey'
            }

            onMounted(() => {
                collection.value.data = []
                capture()
            })

            return {
                collection: collection.value,
                queries,
                updated,
                touch,
                del,
                statusColor,
                ...useI18n(),
                ...useEnums(),
                ...toRefs(state),
            }
        }
    })
</script>
